// Used in resources-container, content-library-container, and student-resources-container

// Angular
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from "@angular/core";
import { SearchParameters } from "@carnegie-learning/angular-instantsearch";

// Services/Types/Helpers
import { AlgoliaSearchClientHelper } from "#core/helpers";
import { LanguageHelper } from "#helpers/language.helper";
import { LocalStorage } from "#services/local-storage.service";
import { PPAssignment as Assignment } from "cli-types";

// Algolia
import { SearchResponse } from "@algolia/client-search";
import { EditionId, SubjectId } from "@carnegie-learning/common-types";
import { Asset, AssetAssociation, Edition } from "@carnegie-learning/cure-types";
import { FeathersServicePaginationParameters } from "@carnegie-learning/data-access-core";
import { SearchClient } from "algoliasearch";

@Component({
    selector: "algolia-search-widget",
    templateUrl: "./algolia-search-widget.component.html",
    styleUrls: ["./algolia-search-widget.component.scss"],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class AlgoliaSearchWidgetComponent implements OnInit, OnChanges {
    algoliaIndexName: string = (<any>window).ALGOLIA_INDEX_NAME;
    config: object;
    searchInput = "";
    mathTypesDescription = "";
    currentResults: Asset[] = [];
    searchParameters?: SearchParameters = {};
    currentPaginationParameters: FeathersServicePaginationParameters<Asset>;
    timer: any; // what other type might a setTimeout be?
    searchSectionLabel = {
        name: "Search Our Library",
        fontSize: "14px",
    };

    @Input() algoliaSearchClient: SearchClient;
    @Input() limit!: number;
    @Input() assetParams!: FeathersServicePaginationParameters<Asset>;
    @Input() editionId: EditionId = "";
    @Input() selectedEdition?: Edition;
    @Input() selectedSubject?: AssetAssociation;
    @Input() subjectId: SubjectId = "";
    @Input() resourcesTabActive = false;
    @Input() isTeacher = true;
    @Input() allCourseAssignments: Assignment[];
    @Input() placeholderText = "Search";
    @Input() isStudent: boolean;

    @Output() algoliaSearchResults = new EventEmitter<Array<Asset>>();
    @Output()
    algoliaPaginationParameters = new EventEmitter<FeathersServicePaginationParameters<Asset>>();

    constructor(
        private languageHelper: LanguageHelper,
        private localStorage: LocalStorage,
        private algoliaSearchClientHelper: AlgoliaSearchClientHelper
    ) {}

    ngOnInit(): void {
        this.config = {
            indexName: this.algoliaIndexName,
            searchClient: this.algoliaSearchClient,
        };
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes["subjectId"]) {
            if (this.languageHelper.isMathProgram(this.subjectId)) {
                this.mathTypesDescription = ", standard domain, or standard code";
            } else {
                this.mathTypesDescription = "";
            }
        }
        if (this.assetParams && this.selectedEdition) {
            const filters = this.algoliaSearchClientHelper.createAlgoliaFilter(
                this.assetParams,
                this.resourcesTabActive,
                this.isTeacher,
                this.selectedEdition,
                this.allCourseAssignments
            );
            if (this.isSearchParamsUpdated(filters)) {
                // here is where we pre-populate the search input field
                this.searchInput = this.assetParams.searchInput || "";
                this.searchParameters = {
                    ...this.searchParameters,
                    hitsPerPage: this.assetParams.limit,
                    page: this.assetParams.skip,
                    query: this.searchInput,
                    filters,
                };
                this.sendAlgoliaSearch();
            }
        }
    }

    onSearch(event: Event) {
        // if (event.type === "input") {
        //     this.searchInput =
        //         (<HTMLFormElement>event.target)?.form?.children[0]?.value || "";
        // } else if (event.type === "reset") {
        //     this.searchInput = "";
        // }
        if (event.type === "keyup") {
            this.searchInput = (<HTMLFormElement>event.target)?.value;
            if (this.timer) {
                clearTimeout(this.timer);
            }
            this.timer = setTimeout(
                function (componentScope) {
                    componentScope.searchParameters = {
                        ...componentScope.searchParameters,
                        // query: componentScope.searchInput,
                        page: 0,
                    };
                    componentScope.sendAlgoliaSearch();
                },
                1500,
                this
            );
        } else if (event.type === "click") {
            this.searchInput = "";
            this.searchParameters = {
                ...this.searchParameters,
                // query: this.searchInput,
                page: 0,
            };
            this.sendAlgoliaSearch();
        }
    }

    sendAlgoliaSearch(onInit?: boolean) {
        const globalResources =
            this.selectedEdition?.globalResources || this.selectedSubject?.globalResources;

        // PAS-11295 Remove global resources and return accurate resources found count
        const facetFilters = globalResources?.map((g) => `assetId: -${g}`);

        if (this.searchParameters) {
            this.algoliaSearchClient
                .search([
                    {
                        indexName: this.algoliaIndexName,
                        params: {
                            query: this.searchInput,
                            hitsPerPage: this.searchParameters.hitsPerPage,
                            maxValuesPerFacet: this.searchParameters.hitsPerPage,
                            page: this.searchParameters.page,
                            filters: this.searchParameters.filters,
                            facetFilters: facetFilters?.join(","),
                        },
                    },
                ])
                .then((data: any) => {
                    const results: SearchResponse = data.results[0];
                    const paramRequiredUpdated =
                        results.nbHits !== this.assetParams?.total ||
                        this.searchInput !== this.assetParams?.searchInput;
                    if (onInit || paramRequiredUpdated) {
                        this.assetParams = {
                            ...this.assetParams,
                            skip: results.page ?? 0,
                            total: results.nbHits,
                            searchInput: this.searchInput,
                        };
                        if (!this.resourcesTabActive) {
                            this.localStorage.set("savedContentLibParams", this.assetParams);
                        }
                        if (
                            JSON.stringify(this.currentPaginationParameters) !==
                            JSON.stringify(this.assetParams)
                        ) {
                            // check b4 emitting, otherwise it make an infinite loop causing app breaking
                            this.currentPaginationParameters = this.assetParams;
                            this.algoliaPaginationParameters.emit(this.assetParams);
                        }
                    }
                    if (
                        JSON.stringify(this.currentResults) !==
                        JSON.stringify((data.results[0] as SearchResponse).hits)
                    ) {
                        this.currentResults = <Array<Asset>>(<any>data.results[0]).hits;
                    }
                    this.algoliaSearchResults.emit(<Array<Asset>>(<any>data.results[0]).hits);
                });
        }
    }
    /* search params should only consider with filters, limit, skip or search input. */
    isSearchParamsUpdated(filters: string): boolean {
        return (
            JSON.stringify(filters) !== JSON.stringify(this.searchParameters?.filters) ||
            this.assetParams.limit !== this.searchParameters?.hitsPerPage ||
            this.assetParams.skip !== this.searchParameters?.page ||
            this.assetParams.searchInput !== this.searchInput
        );
    }
}
